/**
 * The agent page, `/crew/:slug` (slice 1e spec §2): Studio's F5 with the
 * sections today's data fills. The header spans the page; below it, About
 * and Recent chats in the main column, and This week, Remembers, Thinks with
 * and Computer in a narrower side column. Like the Memory page, the two
 * columns come from the page's own width (a container query: the Crew panel
 * can take 400px of a desktop); narrow, it's one column in that order.
 *
 * Every section links to where its data is changed: Memory, Chats, Settings.
 * What each shows comes from `agent-view.ts`.
 */
import { Badge } from "@crew/ui/components/badge";
import { Button, buttonVariants } from "@crew/ui/components/button";
import { Card } from "@crew/ui/components/card";
import { AgentAvatar } from "@crew/ui/components/crew-avatar";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
} from "@crew/ui/components/item";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { StatRow } from "@crew/ui/components/stat-row";
import { StatusDot, StatusLabel } from "@crew/ui/components/status";
import { Eyebrow, PageTitle, SectionHeading, TextLink } from "@crew/ui/components/typography";
import { Fragment, type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Streamdown } from "streamdown";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { useThreads, type ThreadItem } from "../chats/api";
import { ActivityBadge } from "../chats/chats-page";
import { shortTime } from "../chats/format";
import { usePanel } from "../chats/panel-state";
import { useMemoryFile, useMemoryFiles, type MemoryFileMeta } from "../memory/api";
import { counter, editedBy } from "../memory/memory-view";
import { useModels } from "../setup/api";
import { computerView } from "../computer/status";
import { useAgentPage } from "./api";
import {
  firstParagraph,
  identityBody,
  lastWoke,
  memoryLink,
  statusLine,
  thinksWith,
  weekView,
  EVERY_RUN_FILE,
  WHEN_NEEDED_FILES,
  type AgentPage as AgentData,
} from "./agent-view";
import { HOME } from "../../app/home";

type Agent = Me["agents"][number];

/** The route: an unknown slug goes home, as unknown routes do. */
export function AgentRoute({ me }: { me: Me }) {
  const { slug } = useParams();
  const agent = me.agents.find((a) => a.slug === slug);
  if (!agent) return <Navigate to={HOME} replace />;
  return <AgentPage key={agent.id} me={me} agent={agent} />;
}

function AgentPage({ me, agent }: { me: Me; agent: Agent }) {
  const page = useAgentPage(agent.slug);
  const memory = useMemoryFiles();
  const files = memory.data?.files.filter((f) => f.agent_id === agent.id) ?? [];
  const file = (name: string) => files.find((f) => f.name === name);
  return (
    <main
      data-agent-page={agent.slug}
      className="@container/agent flex w-full max-w-4xl flex-col gap-8 px-4 pt-10 pb-16 sm:px-6 md:px-12 lg:max-w-6xl lg:px-8 lg:pt-9 xl:px-12"
    >
      <Header me={me} agent={agent} role={page.data?.agent.role} />
      <div className="flex flex-col gap-8 @3xl/agent:grid @3xl/agent:grid-cols-[minmax(0,1fr)_17rem] @3xl/agent:items-start @3xl/agent:gap-10 @5xl/agent:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="flex min-w-0 flex-col gap-8">
          <About
            agent={agent}
            identity={file("IDENTITY.md")}
            soul={file("SOUL.md")}
            loading={!memory.data}
          />
          <RecentChats me={me} />
        </div>
        <aside className="flex min-w-0 flex-col gap-7">
          <ThisWeek me={me} week={page.data?.week} error={page.error} />
          <Remembers me={me} agent={agent} notes={file("MEMORY.md")} loading={!memory.data} />
          <ThinksWith me={me} />
          <ComputerSection me={me} lastWakeAt={page.data?.computer?.last_wake_at} />
        </aside>
      </div>
    </main>
  );
}

/** Avatar, name, status, role; New chat and Edit in Memory. */
function Header({ me, agent, role }: { me: Me; agent: Agent; role: string | undefined }) {
  const [, setPanel] = usePanel();
  const status = statusLine(agent, me.computer);
  return (
    <header className="flex flex-col gap-4 @2xl/agent:flex-row @2xl/agent:items-start @2xl/agent:gap-[18px]">
      <div className="flex min-w-0 flex-1 items-start gap-4 @2xl/agent:gap-[18px]">
        <AgentAvatar glyph={agent.glyph} tint={agentTint(agent.tint)} size={56} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <PageTitle>{agent.name}</PageTitle>
            <Badge variant="secondary" className="gap-1.5" data-agent-status={status.tone}>
              <StatusDot tone={status.tone} />
              {status.text}
            </Badge>
          </div>
          {role ? <p className="text-body text-copy">{role}</p> : null}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Link
          href={memoryLink("IDENTITY.md", agent.slug)}
          className={buttonVariants({ variant: "outline" })}
        >
          Edit in Memory
        </Link>
        <Button onClick={() => setPanel("open")}>New chat</Button>
      </div>
    </header>
  );
}

/** A section of the main column: a serif heading, with an optional link beside it. */
function MainSection({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <SectionHeading size="md" aside={aside}>
        {title}
      </SectionHeading>
      {children}
    </section>
  );
}

/** A section of the side column: an eyebrow over it, with a link under it. */
function SideSection({
  title,
  link,
  children,
}: {
  title: string;
  link?: { href: string; text: string };
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <Eyebrow>{title}</Eyebrow>
      {children}
      {link ? <TextLink href={link.href}>{link.text}</TextLink> : null}
    </section>
  );
}

/** A file the owner emptied: it says so, and links to it. */
function EmptyFile({ name, href }: { name: string; href: string }) {
  return (
    <p className="text-body text-muted-foreground" data-empty-file={name}>
      {name} is empty. <TextLink href={href}>Write it in Memory</TextLink>
    </p>
  );
}

/** IDENTITY.md, rendered; then SOUL.md's first paragraph under Personality. */
function About({
  agent,
  identity,
  soul,
  loading,
}: {
  agent: Pick<Agent, "slug">;
  identity: MemoryFileMeta | undefined;
  soul: MemoryFileMeta | undefined;
  loading: boolean;
}) {
  const identityHref = memoryLink("IDENTITY.md", agent.slug);
  return (
    <MainSection title="About" aside={<TextLink href={identityHref}>Edit identity</TextLink>}>
      <div data-about className="flex flex-col gap-5">
        <Identity meta={identity} href={identityHref} loading={loading} />
        <Personality meta={soul} href={memoryLink("SOUL.md", agent.slug)} />
      </div>
    </MainSection>
  );
}

/** IDENTITY.md without its title, as Markdown; or that it's empty. */
function Identity({
  meta,
  href,
  loading,
}: {
  meta: MemoryFileMeta | undefined;
  href: string;
  loading: boolean;
}) {
  const file = useMemoryFile(meta?.id);
  if (loading || (meta && !file.data)) return <Spinner className="size-5 text-muted-foreground" />;
  const body = file.data ? identityBody(file.data.content) : null;
  if (!body) return <EmptyFile name="IDENTITY.md" href={href} />;
  return (
    <Streamdown className="space-y-2 text-body text-foreground [&_li]:my-0.5" controls={false}>
      {body}
    </Streamdown>
  );
}

/** SOUL.md's first paragraph, with Edit personality. */
function Personality({ meta, href }: { meta: MemoryFileMeta | undefined; href: string }) {
  const file = useMemoryFile(meta?.id);
  const paragraph = file.data ? firstParagraph(file.data.content) : null;
  const waiting = Boolean(meta && !file.data);
  return (
    <div className="flex flex-col gap-1.5" data-personality>
      <Eyebrow size="sm">Personality</Eyebrow>
      {waiting ? null : paragraph ? (
        <p className="text-body text-copy">{paragraph}</p>
      ) : (
        <EmptyFile name="SOUL.md" href={href} />
      )}
      <TextLink href={href} className="self-start">
        Edit personality →
      </TextLink>
    </div>
  );
}

/** The five newest threads; today every one is the Chief of Staff's. */
function RecentChats({ me }: { me: Me }) {
  const threads = useThreads("everyone");
  const [, setPanel] = usePanel();
  const recent = threads.data?.pages[0]?.threads.slice(0, 5);
  return (
    <MainSection
      title="Recent chats"
      aside={recent?.length ? <TextLink href="/chats">All chats →</TextLink> : undefined}
    >
      {threads.error ? (
        <p className="text-body text-muted-foreground">{errorHint(threads.error)}</p>
      ) : !recent ? (
        <Spinner className="size-5 text-muted-foreground" />
      ) : recent.length === 0 ? (
        <div
          data-recent-empty
          className="flex flex-col items-start gap-3 rounded-well bg-muted p-5"
        >
          <p className="text-body text-muted-foreground">No chats yet</p>
          <Button variant="outline" size="sm" onClick={() => setPanel("open")}>
            Start one
          </Button>
        </div>
      ) : (
        <Card className="gap-0 py-0">
          <ItemGroup className="gap-0">
            {recent.map((t, i) => (
              <Fragment key={t.id}>
                {i > 0 ? <ItemSeparator className="my-0" /> : null}
                <ChatRow thread={t} timeZone={me.workspace.timezone} />
              </Fragment>
            ))}
          </ItemGroup>
        </Card>
      )}
    </MainSection>
  );
}

function ChatRow({ thread, timeZone }: { thread: ThreadItem; timeZone: string }) {
  return (
    <Item
      render={<Link href={`/chats/${thread.id}`} />}
      className="flex-nowrap items-start rounded-none px-4 py-3 hover:bg-foreground/[0.03]"
      data-recent-chat={thread.id}
    >
      <ItemContent className="min-w-0 gap-1">
        <ItemTitle className="w-full min-w-0">
          <span className="truncate">{thread.title}</span>
        </ItemTitle>
        {thread.preview ? (
          <ItemDescription className="line-clamp-1">{thread.preview}</ItemDescription>
        ) : null}
      </ItemContent>
      <ItemActions className="flex-col items-end gap-1.5">
        <span className="text-xs whitespace-nowrap text-muted-foreground">
          {shortTime(thread.last_message_at, timeZone)}
        </span>
        <ActivityBadge activity={thread.activity} />
      </ItemActions>
    </Item>
  );
}

/** The last 7 days, from `GET /api/agents/:slug`. */
function ThisWeek({
  me,
  week,
  error,
}: {
  me: Me;
  week: AgentData["week"] | undefined;
  error: unknown;
}) {
  const view = week ? weekView(week, me.workspace.timezone) : null;
  return (
    <SideSection title="This week">
      <div data-week>
        {error ? (
          <p className="text-body text-muted-foreground">{errorHint(error)}</p>
        ) : !view ? (
          <Spinner className="size-5 text-muted-foreground" />
        ) : view.empty ? (
          <p className="text-body text-muted-foreground">Nothing yet this week.</p>
        ) : (
          <div className="flex flex-col gap-2.5">
            <StatRow stats={view.stats} />
            {view.last ? <p className="text-meta text-muted-foreground">{view.last}</p> : null}
          </div>
        )}
      </div>
    </SideSection>
  );
}

/** What loads every run (its Notes, with their size and last change, and Context), and what's read when needed. */
function Remembers({
  me,
  agent,
  notes,
  loading,
}: {
  me: Me;
  agent: Pick<Agent, "slug">;
  notes: MemoryFileMeta | undefined;
  loading: boolean;
}) {
  return (
    <SideSection title="Remembers">
      <ItemGroup className="gap-0" data-remembers>
        <p className="text-meta text-muted-foreground" data-remembers-group="every-run">
          Every run
        </p>
        <Item className="rounded-none px-0 py-2">
          <ItemContent>
            <ItemTitle>Notes</ItemTitle>
            <ItemDescription className="line-clamp-none">
              {loading ? "…" : notes ? editedBy(notes, me) : "Not set up yet."}
            </ItemDescription>
          </ItemContent>
          {notes ? (
            <ItemActions className="text-meta text-muted-foreground tabular">
              {counter(notes.length).text}
            </ItemActions>
          ) : null}
        </Item>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <TextLink href={memoryLink("MEMORY.md", agent.slug)}>Open notes →</TextLink>
          <TextLink href={memoryLink(EVERY_RUN_FILE.name)}>{EVERY_RUN_FILE.title}</TextLink>
        </div>
        <ItemSeparator className="my-3" />
        <p className="text-meta text-muted-foreground" data-remembers-group="when-needed">
          When needed
        </p>
        <p className="pt-0.5 pb-1.5 text-xs text-muted-foreground">
          Read before writing for the workspace, or when the work involves someone else.
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {WHEN_NEEDED_FILES.map((f) => (
            <TextLink key={f.name} href={memoryLink(f.name)}>
              {f.title}
            </TextLink>
          ))}
        </div>
      </ItemGroup>
    </SideSection>
  );
}

/** The default model, the fallbacks, and the accounts behind them. */
function ThinksWith({ me }: { me: Me }) {
  const catalog = useModels();
  const view = thinksWith(me.computer?.ai, catalog.data, me.workspace.timezone);
  return (
    <SideSection
      title="Thinks with"
      // Settings → AI is the owner's (slice 6a §6.5).
      link={me.user.role === "owner" ? { href: "/settings#ai", text: "AI settings →" } : undefined}
    >
      <div data-thinks-with={view.connected ? "connected" : "none"}>
        {!view.connected ? (
          <p className="text-body text-muted-foreground">Not connected</p>
        ) : (
          <ItemGroup className="gap-0">
            <Row label="Default" value={view.model ?? "Not chosen"} />
            {view.fallbacks.map((m, i) => (
              <Row key={m} label={`Fallback ${i + 1}`} value={m} />
            ))}
            {view.accounts.map((a) => (
              <Fragment key={a.id}>
                <ItemSeparator className="my-0" />
                <Item className="rounded-none px-0 py-2.5" data-account={a.status.tone}>
                  <ItemContent className="min-w-0">
                    <ItemTitle className="w-full min-w-0">
                      <span className="truncate">{a.label}</span>
                    </ItemTitle>
                    <ItemDescription>{a.provider}</ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <StatusLabel tone={a.status.tone}>{a.status.text}</StatusLabel>
                  </ItemActions>
                </Item>
              </Fragment>
            ))}
          </ItemGroup>
        )}
      </div>
    </SideSection>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * The computer's status, in the sidebar's words, and when it last woke. No
 * link: Settings has no computer section yet, and Retry is on Chats.
 */
function ComputerSection({ me, lastWakeAt }: { me: Me; lastWakeAt: string | null | undefined }) {
  const view = computerView(me.computer);
  const woke = lastWoke(lastWakeAt, me.workspace.timezone);
  return (
    <SideSection title="Computer">
      <div data-computer={me.computer?.status ?? "none"} className="flex flex-col gap-1">
        <StatusLabel tone={view.tone} emphasis>
          {view.title}
        </StatusLabel>
        <p className="pl-4 text-meta text-muted-foreground">{view.detail}</p>
        {woke ? <p className="pl-4 text-meta text-muted-foreground">{woke}</p> : null}
      </div>
    </SideSection>
  );
}
